import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  dashboardWidgetState,
  selectAlertsData,
  selectWeeklyPlan,
  selectWeeklyPlanIsLoading,
} from "@/store/slices";
import { useTranslation } from "react-i18next";
import { AlertUrgency } from "@/enums/Alerts";
import { isYesterday } from "date-fns";
import { selectAssessmentStatusWidgetData } from "@/features/dashboard/store/slices/assessmentStatusWidgetSlice";
import { AsyncLoadingState } from "@/features/dashboard/enums";
import { findAssessmentWindow } from "@/features/dashboard/utils";
import { useAssessmentPeriodWindows } from "./useAssessmentPeriods";

// Order of priority for messages
const MESSAGE_TYPES = {
  GREETING: "greeting",
  GENERIC: "generic", // optional messages, used when data is loading
  STATUS: "status",
  ALERT: "alert",
};

const MESSAGE_TYPES_PRIORITY = {
  GREETING: 1,
  GENERIC: 2,
  STATUS: 3,
  ALERT: 4,
};

export const useGreetingMessage = () => {
  const { t, i18n, ready } = useTranslation("amira_greeting_message");
  const { data: weeklyPlanData } = useSelector(selectWeeklyPlan);
  const { alerts, loading: alertsLoading } = useSelector(selectAlertsData);
  const weeklyPlanLoading = useSelector(selectWeeklyPlanIsLoading);
  const { loading: assessmentLoading, data: assessmentData } = useSelector(
    selectAssessmentStatusWidgetData
  );
  const assessmentCalendar = useAssessmentPeriodWindows();
  const { studentsNeedingAttention } = useSelector(dashboardWidgetState);
  const [greetingMessages, setGreetingMessages] = useState<
    { key: string; message: string; priority: number }[]
  >(() => []);
  const [playedMessageTypes, setPlayedMessageTypes] = useState(
    new Set<string>()
  );
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const dismissMessages = () => {
    setGreetingMessages([]);
  };

  const dequeueMessage = () => {
    if (greetingMessages.length > 0) {
      const playedMessageType = greetingMessages[0].key;
      setPlayedMessageTypes(prevPlayed =>
        new Set(prevPlayed).add(playedMessageType)
      );
    }
    setGreetingMessages(prev => prev.slice(1));
  };

  const getRandomMessage = (messages: string[]) => {
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const getStatusMessage = () => {
    const messages = [];

    /**
     * Assessment VO:
        Window closing soon and at least 1 student without an assessment
        Window opening soon (within 3 days)
        Window recently closed (within 3 days)
    */
    if (assessmentLoading === AsyncLoadingState.SUCCESS && assessmentData) {
      const { totalAssessmentCount, completeCount } =
        assessmentData.classroomAssessmentStatusData.data;
      const assessmentWindowStatusData = findAssessmentWindow(
        assessmentCalendar.assessmentPeriodWindows
      );
      const {
        daysLeftInCurrentAssessmentWindow,
        daysUntilNextAssessmentWindowOpens,
      } = assessmentWindowStatusData;
      if (
        daysLeftInCurrentAssessmentWindow != null &&
        daysLeftInCurrentAssessmentWindow <= 3 &&
        daysLeftInCurrentAssessmentWindow >= 0 &&
        totalAssessmentCount - completeCount > 0
      ) {
        messages.push(t("assessment_closing_soon"));
      } else if (
        daysUntilNextAssessmentWindowOpens != null &&
        daysUntilNextAssessmentWindowOpens <= 3 &&
        daysUntilNextAssessmentWindowOpens >= 0
      ) {
        messages.push(t("assessment_window_opening_soon"));
      } else if (
        daysLeftInCurrentAssessmentWindow != null &&
        daysLeftInCurrentAssessmentWindow < 0 &&
        daysLeftInCurrentAssessmentWindow >= -3
      ) {
        messages.push(t("assessment_window_recently_closed"));
      }
    }

    /*
      Instruct VO:
        75% completion on yesterday's lesson
        Lesson plan underway
    */
    const { tracks = [] } = weeklyPlanData?.curriculum ?? {};
    let foundLesson = false;
    let foundLessonWith75Percent = false;
    for (const track of tracks) {
      const { units = [] } = track;
      for (const unit of units) {
        const { lessons = [] } = unit;
        for (const lesson of lessons) {
          const { completionPercentage, dateFrom } = lesson;
          if (isYesterday(new Date(dateFrom))) {
            if (completionPercentage >= 75) {
              foundLessonWith75Percent = true;
            } else if (completionPercentage > 0) {
              foundLesson = true;
            }
            if (foundLessonWith75Percent && foundLesson) break;
          }
        }
        if (foundLessonWith75Percent && foundLesson) break;
      }
      if (foundLessonWith75Percent && foundLesson) break;
    }
    if (foundLessonWith75Percent) {
      messages.push(t("instruct_percent_complete"));
    } else if (foundLesson) {
      messages.push(t("instruct_lesson_underway"));
    }

    /*
      Tutor VO:
        Behind weekly goals - Mid week and at least 1 student below 10 mins total time
        On track for weekly goals: Mid week and no student is in red
     */
    const midWeek = new Date().getDay() === 3;
    if (midWeek && studentsNeedingAttention) {
      if (studentsNeedingAttention > 0) {
        messages.push(t("tutor_behind_weekly_goal"));
      } else {
        messages.push(t("tutor_on_track_weekly_goal"));
      }
    }

    if (messages.length > 0) {
      return {
        key: MESSAGE_TYPES.STATUS,
        message: getRandomMessage(messages),
        priority: MESSAGE_TYPES_PRIORITY.STATUS,
      };
    }

    return null;
  };

  const getAlertMessage = () => {
    if (alerts) {
      const urgentAlerts = alerts.filter(
        alert =>
          alert.attentionLevel === AlertUrgency.Urgent ||
          alert.attentionLevel === AlertUrgency.Rare
      );
      let m = "";
      if (urgentAlerts.length > 1) {
        m = t("alert_multiple");
      } else if (urgentAlerts.length === 1 && urgentAlerts[0].alertVO) {
        m = urgentAlerts[0].alertVO; // actual VO comes from the backend
      }
      if (m.length > 0) {
        return {
          key: MESSAGE_TYPES.ALERT,
          message: m,
          priority: MESSAGE_TYPES_PRIORITY.ALERT,
        };
      }
    }
    return null;
  };

  const getGenericMessage = () => {
    return {
      key: MESSAGE_TYPES.GENERIC,
      message: getRandomMessage([
        t("generic_vo_1"),
        t("generic_vo_2"),
        t("generic_vo_3"),
      ]),
      priority: MESSAGE_TYPES_PRIORITY.GENERIC,
    };
  };

  useEffect(() => {
    // Only proceed if translations for the specified namespace are ready and t is a function
    if (!ready || typeof t !== "function") {
      return;
    }

    const greetings = [t("hello")];
    const currentHour = new Date().getHours();
    let greetingText = "";

    if (currentHour >= 0 && currentHour < 12) {
      // Midnight to 11:59 AM
      greetingText = t("good_morning");
    } else if (currentHour >= 12 && currentHour < 17) {
      // Noon to 4:59 PM
      greetingText = t("good_afternoon");
    } else {
      // 5 PM to 11:59 PM
      greetingText = t("good_evening");
    }

    if (greetingText) {
      greetings.push(greetingText);
    }

    setGreetingMessages([
      {
        key: MESSAGE_TYPES.GREETING,
        message: getRandomMessage(greetings),
        priority: MESSAGE_TYPES_PRIORITY.GREETING,
      },
    ]);
  }, [t, i18n, ready]); // Use ready from useTranslation, and i18n for language logging if needed

  useEffect(() => {
    if (greetingMessages.length === 0 && playedMessageTypes.size === 0) {
      return;
    }
    const currentQueue = greetingMessages;
    const firstMessageInQueue =
      currentQueue.length > 0 ? currentQueue[0] : null;

    const hasAlerts = alerts && Array.isArray(alerts) && alerts.length > 0;
    const hasWeeklyPlan =
      weeklyPlanData &&
      typeof weeklyPlanData === "object" &&
      Object.keys(weeklyPlanData).length > 0;

    const potentialMessages: {
      key: string;
      message: string;
      priority: number;
    }[] = [];

    // 1. Add GREETING if applicable
    if (
      firstMessageInQueue &&
      firstMessageInQueue.key === MESSAGE_TYPES.GREETING &&
      !playedMessageTypes.has(MESSAGE_TYPES.GREETING)
    ) {
      potentialMessages.push(firstMessageInQueue);
    }

    // 2. Add STATUS message if applicable
    if (!playedMessageTypes.has(MESSAGE_TYPES.STATUS)) {
      const existingUnplayedStatus = greetingMessages.find(m => m.key === MESSAGE_TYPES.STATUS);

      if (existingUnplayedStatus) {
        potentialMessages.push(existingUnplayedStatus);
      } else {
        if (
          !weeklyPlanLoading &&
          assessmentLoading === AsyncLoadingState.SUCCESS &&
          hasWeeklyPlan &&
          assessmentCalendar.loading === false &&
          studentsNeedingAttention
        ) {
          const statusMessage = getStatusMessage();
          if (statusMessage) {
            potentialMessages.push(statusMessage);
          }
        }
      }
    }

    // 3. Add ALERT message if applicable
    if (!playedMessageTypes.has(MESSAGE_TYPES.ALERT)) {
      const existingUnplayedAlert = greetingMessages.find(m => m.key === MESSAGE_TYPES.ALERT);

      if (existingUnplayedAlert) {
        potentialMessages.push(existingUnplayedAlert);
      } else {
        if (
          !alertsLoading &&
          hasAlerts
        ) {
          const alertMessage = getAlertMessage();
          if (alertMessage) {
            potentialMessages.push(alertMessage);
          }
        }
      }
    }

    // 4. Add GENERIC message if applicable
    if (!playedMessageTypes.has(MESSAGE_TYPES.GENERIC)) {
      const existingUnplayedGeneric = greetingMessages.find(m => m.key === MESSAGE_TYPES.GENERIC);

      if (existingUnplayedGeneric) {
        potentialMessages.push(existingUnplayedGeneric);
      } else {
        // Check if a STATUS or ALERT message is already in potentialMessages or has been played
        const statusOrAlertIsPresentOrPlayed =
          potentialMessages.some(
            msg => msg.key === MESSAGE_TYPES.STATUS || msg.key === MESSAGE_TYPES.ALERT
          ) ||
          playedMessageTypes.has(MESSAGE_TYPES.STATUS) ||
          playedMessageTypes.has(MESSAGE_TYPES.ALERT);

        if (
          playedMessageTypes.has(MESSAGE_TYPES.GREETING) &&
          (potentialMessages.length === 0 ||
            (potentialMessages.length === 1 &&
              potentialMessages[0].key === MESSAGE_TYPES.GREETING)) &&
          (weeklyPlanLoading || alertsLoading || assessmentLoading === AsyncLoadingState.LOADING) && // Added assessmentLoading check
          !statusOrAlertIsPresentOrPlayed // Use the new combined check
        ) {
          potentialMessages.push(getGenericMessage());
        }
      }
    }

    // Deduplicate by key (keeping the first instance, though order doesn't matter before sort)
    const uniquePotentialMessages = potentialMessages.filter(
      (item, index, self) => index === self.findIndex(t => t.key === item.key)
    );

    // Sort messages by priority
    const finalNewQueue = uniquePotentialMessages.sort(
      (a, b) => a.priority - b.priority
    );

    if (JSON.stringify(currentQueue) !== JSON.stringify(finalNewQueue)) {
      setGreetingMessages(finalNewQueue);
    }

    setIsDataLoaded(alertsLoading !== AsyncLoadingState.LOADING && assessmentLoading !== AsyncLoadingState.LOADING && weeklyPlanLoading !== true)
  }, [
    weeklyPlanData,
    alerts,
    weeklyPlanLoading,
    alertsLoading,
    greetingMessages,
    playedMessageTypes,
  ]);

  return {
    greetingMessages,
    dismissMessages,
    dequeueMessage,
    isDataLoaded,
  };
};
